import { Injectable, OnDestroy, inject } from '@angular/core';
import { environment } from '@env/environment';
import { AuthService } from '@core/services/api/auth.service';
import type { ClientLogEvent, CreateClientErrorLogsBatchDto } from './client-log-event.model';

const QUEUE_STORAGE_KEY = 'electa.clientLogs.queue';
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 20;
const MAX_QUEUE_SIZE = 50;
const MAX_RETRY_ATTEMPTS = 5;
const CIRCUIT_OPEN_MS = 30_000;
const ENDPOINT_PATH = '/client-error-logs/batch';

interface QueuedClientLogEvent {
  event: ClientLogEvent;
  attempts: number;
}

@Injectable({ providedIn: 'root' })
export class RemoteClientLogService implements OnDestroy {
  private readonly auth = inject(AuthService);
  private queue: QueuedClientLogEvent[] = [];
  private flushInterval: ReturnType<typeof setInterval> | null = null;
  private circuitOpenUntil = 0;
  private flushInProgress = false;
  private initialized = false;

  record(event: ClientLogEvent): void {
    try {
      if (!environment.logging.remoteEnabled) return;

      this.ensureInitialized();
      this.queue.push({ event, attempts: 0 });

      if (this.queue.length > MAX_QUEUE_SIZE) {
        this.queue = this.queue.slice(-MAX_QUEUE_SIZE);
      }

      this.persistQueue();

      if (this.queue.length >= MAX_BATCH_SIZE) {
        this.flush();
      }
    } catch {
      // Logging transport must never throw back into application code.
    }
  }

  ngOnDestroy(): void {
    try {
      this.stopFlushInterval();
      this.flush(true);
    } catch {
      // ignore shutdown logging failures
    }
  }

  private ensureInitialized(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.queue = this.loadPersistedQueue();
    this.startFlushInterval();

    if (typeof window !== 'undefined') {
      try {
        window.addEventListener('beforeunload', () => this.flush(true));
        window.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') {
            this.flush(true);
          }
        });
      } catch {
        // ignore browser lifecycle hook failures
      }
    }
  }

  private flush(isUnload = false): void {
    if (this.queue.length === 0 || this.flushInProgress) return;
    if (!isUnload && Date.now() < this.circuitOpenUntil) return;

    let token: string | null;
    try {
      token = this.auth.getToken();
    } catch {
      return;
    }
    if (!token) return;

    const items = this.queue.slice(0, MAX_BATCH_SIZE);
    this.queue = this.queue.slice(items.length);
    this.persistQueue();

    let body: string;
    try {
      const batch: CreateClientErrorLogsBatchDto = {
        events: items.map((item) => item.event),
      };
      body = JSON.stringify(batch);
    } catch {
      return;
    }

    this.flushInProgress = true;
    try {
      fetch(`${environment.apiUrl}${ENDPOINT_PATH}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body,
        keepalive: isUnload,
      })
        .then((response) => {
          if (response.ok) {
            this.circuitOpenUntil = 0;
            return;
          }
          if (this.shouldRetryStatus(response.status)) {
            this.openCircuit();
            this.requeue(items);
          }
        })
        .catch(() => {
          if (!isUnload) {
            this.openCircuit();
            this.requeue(items);
          }
        })
        .finally(() => {
          this.flushInProgress = false;
        });
    } catch {
      this.flushInProgress = false;
      if (!isUnload) {
        this.openCircuit();
        this.requeue(items);
      }
    }
  }

  private requeue(items: QueuedClientLogEvent[]): void {
    try {
      const retryableItems = items
        .map((item) => ({ ...item, attempts: item.attempts + 1 }))
        .filter((item) => item.attempts <= MAX_RETRY_ATTEMPTS);

      this.queue = [...retryableItems, ...this.queue].slice(0, MAX_QUEUE_SIZE);
      this.persistQueue();
    } catch {
      // drop on malformed queue state rather than throwing
    }
  }

  private startFlushInterval(): void {
    if (this.flushInterval) return;
    try {
      this.flushInterval = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
    } catch {
      this.flushInterval = null;
    }
  }

  private stopFlushInterval(): void {
    if (!this.flushInterval) return;
    clearInterval(this.flushInterval);
    this.flushInterval = null;
  }

  private persistQueue(): void {
    if (typeof window === 'undefined') return;
    try {
      if (this.queue.length === 0) {
        sessionStorage.removeItem(QUEUE_STORAGE_KEY);
      } else {
        sessionStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(this.queue));
      }
    } catch {
      // ignore storage failures
    }
  }

  private loadPersistedQueue(): QueuedClientLogEvent[] {
    if (typeof window === 'undefined') return [];
    try {
      const raw = sessionStorage.getItem(QUEUE_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((item) => this.toQueuedItem(item))
        .filter((item): item is QueuedClientLogEvent => item !== null)
        .slice(-MAX_QUEUE_SIZE);
    } catch {
      return [];
    }
  }

  private toQueuedItem(value: unknown): QueuedClientLogEvent | null {
    if (!value || typeof value !== 'object') return null;
    const maybeQueued = value as Partial<QueuedClientLogEvent> & Partial<ClientLogEvent>;
    const event = maybeQueued.event ?? maybeQueued;
    if (!event || typeof event !== 'object') return null;
    if (typeof (event as ClientLogEvent).message !== 'string') return null;
    if (typeof (event as ClientLogEvent).severity !== 'string') return null;
    if (typeof (event as ClientLogEvent).source !== 'string') return null;

    return {
      event: event as ClientLogEvent,
      attempts: typeof maybeQueued.attempts === 'number' ? maybeQueued.attempts : 0,
    };
  }

  private shouldRetryStatus(status: number): boolean {
    return status === 408 || status === 429 || status >= 500;
  }

  private openCircuit(): void {
    this.circuitOpenUntil = Date.now() + CIRCUIT_OPEN_MS;
  }
}
